import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Grid3X3, List, Camera, BellOff, Radio, Check, Zap, Volume2, Settings, VideoIcon, Edit, Link as LinkIcon, SearchCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DeviceSnapshot } from '@/components/device/DeviceSnapshot';
import { SecondaryDeviceFullDto, SpeakerDto } from '@/lib/api/types.gen';
import { Link } from 'react-router-dom';
import { HoverStateContent } from '@/components/ui/toggle-button';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { RoutePath } from '@/lib/routes';
import { DeviceCapabilitiesGroup } from '@/components/device/DeviceCapabilitiesBadge';
import { DeviceSnapshotWithReadonlyGrid } from '@/components/device/DeviceReadonlyGrids';
import { HoverCard, HoverCardContent } from '@/components/ui/hover-card';
import { HoverCardPortal, HoverCardTrigger } from '@radix-ui/react-hover-card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RefreshCamerasButton } from './RefreshCamerasButton';
import { MoveDeviceButton } from '../DevicePage/components/MoveDeviceDialog';
import { useDeviceFilterToggle } from '@/lib/devices/toggle';
import { Pagination } from '@/components/navigation/Pagination';




import { X, ScanHeart } from 'lucide-react';
import { SpeakerComponentsModal } from '@/features/Devices/DevicePage/components/SpeakerComponentsModal';
import { LensComponentsModal } from '@/features/Devices/DevicePage/components/LensComponentsModal';
import { RelayComponentsModal } from '@/features/Devices/DevicePage/components/RelayComponentsModal';
import { LiveTesterDialog } from '@/app/Devices/components/LiveTesterDialog';

// Temporary lens interface until API is updated
interface LensDto {
  id?: string;
  name?: string;
}

// Extended camera type that includes speakers and lenses
type ExtendedCamera = SecondaryDeviceFullDto & {
  speakers?: SpeakerDto[];
  lenses?: LensDto[];
  integrationType?: string;
};

interface CameraListProps {
  deviceId: string;
  projectId?: string;
  cameras: SecondaryDeviceFullDto[];
  onCameraToggle?: (cameraId: string) => void;
  onCameraToggleMany?: (cameraIds: string[], enabled: boolean) => void;
}

type ComponentType = 'speakers' | 'lenses' | 'relays';

function ComponentCounts({ 
  camera, 
  onManageComponents,
  allDevices = []
}: { 
  camera: ExtendedCamera; 
  onManageComponents: (camera: ExtendedCamera, type: ComponentType) => void;
  allDevices?: ExtendedCamera[];
}) {
  // For NVR devices: 1 lens (hardcoded), max 1 speaker, 0 relays (not supported)
  const isNvrCamera = camera.integrationType?.toLowerCase() === 'nvr' || 
                      camera.integrationType?.toLowerCase() === 'camera' ||
                      !camera.integrationType; // Assume cameras if no integrationType
  
  const onboardRelaysCount = isNvrCamera ? 0 : (camera.relays?.length || 0);
  const lensesCount = isNvrCamera ? 1 : (camera.lenses?.length || 0);
  const onboardSpeakersCount = isNvrCamera 
    ? Math.min(camera.speakers?.length || 0, 1) 
    : (camera.speakers?.length || 0);

  // Calculate associated components from other devices
  // Components are associated if they belong to another device but include this device in relatedComponentGroups
  const currentComponentGroupId = camera.id;
  
  const associatedSpeakers = currentComponentGroupId ? allDevices.flatMap(device => 
    device.speakers?.filter(speaker => 
      speaker.componentGroupId && 
      speaker.componentGroupId !== currentComponentGroupId && 
      speaker.relatedComponentGroups?.includes(currentComponentGroupId)
    ) || []
  ) : [];
  
  const associatedRelays = currentComponentGroupId ? allDevices.flatMap(device => 
    device.relays?.filter(relay => 
      relay.componentGroupId && 
      relay.componentGroupId !== currentComponentGroupId && 
      relay.relatedComponentGroups?.includes(currentComponentGroupId)
    ) || []
  ) : [];

  const associatedSpeakersCount = associatedSpeakers.length;
  const associatedRelaysCount = associatedRelays.length;
  
  // Calculate total counts (onboard + linked)
  const totalSpeakersCount = onboardSpeakersCount + associatedSpeakersCount;
  const totalRelaysCount = onboardRelaysCount + associatedRelaysCount;


  const ComponentButton = ({ 
    type, 
    count, 
    icon: Icon, 
    disabled = false, 
    tooltip, 
    className = '' 
  }: {
    type: ComponentType;
    count: number;
    icon: any;
    disabled?: boolean;
    tooltip?: string;
    className?: string;
  }) => {
    const button = (
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        className={cn(
          "h-6 w-14 px-3 text-xs",
          disabled ? "cursor-not-allowed" : "",
          className
        )}
        onClick={disabled ? undefined : (e) => {
          e.preventDefault();
          e.stopPropagation();
          onManageComponents(camera, type);
        }}
      >
        <Icon className="h-3 w-3 mr-auto" />
        {count}
      </Button>
    );

    if (tooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return button;
  };


  return (
    <TooltipProvider>
      <div className="flex flex-col items-center gap-1">
        <div className="flex flex-wrap gap-1 justify-center">
          <ComponentButton
            type="lenses"
            count={lensesCount}
            icon={VideoIcon}
            disabled={isNvrCamera}
            tooltip={isNvrCamera ? "Not yet supported on this device" : undefined}
            className={lensesCount > 0 
              ? 'bg-blue-500/20 border-blue-500/50 text-blue-200 hover:bg-blue-500/30' 
              : 'bg-gray-500/20 border-gray-500/50 text-gray-400 hover:bg-gray-500/30'
            }
          />
          
          <ComponentButton
            type="speakers"
            count={totalSpeakersCount}
            icon={Volume2}
            className={totalSpeakersCount > 0 
              ? 'bg-green-500/20 border-green-500/50 text-green-200 hover:bg-green-500/30' 
              : 'bg-gray-500/20 border-gray-500/50 text-gray-400 hover:bg-gray-500/30'
            }
          />
          
          <ComponentButton
            type="relays"
            count={totalRelaysCount}
            icon={Zap}
            disabled={isNvrCamera}
            tooltip={isNvrCamera ? "Not yet supported on this device" : undefined}
            className={totalRelaysCount > 0 
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-200 hover:bg-amber-500/30' 
              : 'bg-gray-500/20 border-gray-500/50 text-gray-400 hover:bg-gray-500/30'
            }
          />
        </div>
      </div>
    </TooltipProvider>
  );
}

type ViewMode = 'table' | 'gallery';

function CameraTableToggle({
  camera,
  onToggle,
}: {
  camera: SecondaryDeviceFullDto;
  onToggle?: (cameraId: string) => void;
}) {
  const isEnabled = camera.monitored ?? false;

  const handleToggle = () => {
    if (!camera.id) return;

    onToggle?.(camera.id);
  };

  return (
    <Switch
      checked={isEnabled}
      onCheckedChange={handleToggle}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function CameraGalleryToggle({
  camera,
  onToggle,
}: {
  camera: SecondaryDeviceFullDto;
  onToggle?: (cameraId: string) => void;
}) {
  const isEnabled = camera.monitored ?? false;

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!camera.id) return;

    onToggle?.(camera.id);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleToggle}
      className={cn(
        'h-8 w-full rounded-none rounded-b-lg text-xs font-medium duration-0',
        isEnabled
          ? 'bg-lime text-black hover:bg-green-light hover:text-white'
          : 'bg-green-light text-white hover:bg-green-light/90 hover:text-white'
      )}
    >
      <HoverStateContent
        on={isEnabled}
        onContent={
          <div className="flex items-center">
            <Radio className="mr-1 h-4 w-4" />
            Monitoring is enabled
          </div>
        }
        offContent={
          <div className="flex items-center">
            <BellOff className="mr-1 h-4 w-4" />
            Monitoring is paused
          </div>
        }
        onHoverContent={
          <div className="flex items-center">
            <BellOff className="mr-1 h-4 w-4" />
            Pause monitoring
          </div>
        }
        offHoverContent={
          <div className="flex items-center">
            <Radio className="mr-1 h-4 w-4" />
            Resume monitoring
          </div>
        }
        className="flex items-center justify-center"
      />
    </Button>
  );
}

function CameraTableView({ deviceId, cameras, onCameraToggle, onManageComponents, allCameras, onLiveTest }: CameraListProps & {
  onManageComponents: (camera: ExtendedCamera, type: ComponentType) => void;
  allCameras: ExtendedCamera[];
  onLiveTest: (cameraId: string) => void;
}) {
  return (
    <div className="rounded-xl border bg-[#111]">
      <Table>
        <TableHeader>
          <TableRow className="border-gray-700 hover:bg-card2">
            <TableHead className="w-12 text-gray-300">Enabled</TableHead>
            <TableHead className="text-gray-300">Preview</TableHead>
            <TableHead className="text-gray-300">Name</TableHead>
            <TableHead className="text-gray-300">Vendor</TableHead>
            <TableHead className="text-gray-300">Model</TableHead>
            <TableHead className="text-gray-300">Capabilities</TableHead>
            <TableHead className="text-center text-gray-300">Components</TableHead>
            <TableHead className="text-center text-gray-300">Grids</TableHead>
            <TableHead className="text-center text-gray-300">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cameras.map((camera) => {
            const isEnabled = camera.monitored ?? false;

            return (
              <TableRow
                key={camera.id}
                className={cn(
                  'border-gray-700 transition-opacity hover:bg-green-dark/20',
                  !isEnabled && '[&>td]:opacity-70'
                )}
              >
                <TooltipProvider> 
                  <TableCell className="w-12">
                    <CameraTableToggle camera={camera} onToggle={onCameraToggle} />
                  </TableCell>
                  <TableCell className="w-24">
                    <HoverCard closeDelay={50}>
                      <HoverCardTrigger>
                        <div className="flex h-24 w-32 items-center justify-center rounded bg-gray-800">
                          <DeviceSnapshot
                            deviceId={camera.id}
                            className="h-full w-full rounded object-cover"
                            withLoadingText={false}
                          />
                        </div>
                      </HoverCardTrigger>
                      <HoverCardPortal>
                        <HoverCardContent
                          className={cn("max-h-[600px] w-full max-w-[800px] bg-black p-0")}
                          side="right"
                          sideOffset={20}
                          // Disable scale animations so polygons are drawn on at correct size
                          animateScale={false}
                        >
                          <DeviceSnapshotWithReadonlyGrid {...camera} />
                        </HoverCardContent>
                      </HoverCardPortal>
                    </HoverCard>
                  </TableCell>
                  <TableCell className="text-lg font-semibold text-white p-4">
                    {camera.name || <span className="opacity-50">–</span>}
                  </TableCell>
                  <TableCell className="whitespace-break-spaces text-gray-300 md:min-w-[200px]">
                    {camera.vendor || <span className="opacity-50">–</span>}
                  </TableCell>
                  <TableCell className="whitespace-break-spaces text-gray-300 md:min-w-[200px]">
                    {camera.model || <span className="opacity-50">–</span>}
                  </TableCell>
                  <TableCell className="md:min-w-[200px]">
                    <DeviceCapabilitiesGroup capabilities={camera.capabilities} />
                  </TableCell>
                  <TableCell align="center" className="md:min-w-[120px]">
                    <ComponentCounts camera={camera as ExtendedCamera} onManageComponents={onManageComponents} allDevices={allCameras} />
                  </TableCell>
                  <TableCell align="center">
                    {camera.activationWkt == null ? (
                      <span className="opacity-50">–</span>
                    ) : (
                      <Check className="h-4 w-4 text-emerald-500" />
                    )}
                  </TableCell>
                  <TableCell align="center" className="md:min-w-[80px]">
                    <div className="flex flex-row items-center justify-center gap-1">
                      <Link to={RoutePath.DeviceCamera(deviceId, camera.id ?? '')}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                  <Edit className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="bg-[#111] text-white">
                            <p>
                              Edit
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </Link>
                      <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => onLiveTest(camera.id ?? '')}
                            >
                              <ScanHeart className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="bg-[#111] text-white">
                            <p>
                              Live Test
                            </p>
                          </TooltipContent>
                        </Tooltip>
                    </div>
                  </TableCell>
                </TooltipProvider>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function CameraGalleryView({ cameras, onCameraToggle, deviceId, onManageComponents, allCameras }: CameraListProps & { 
  onManageComponents: (camera: ExtendedCamera, type: ComponentType) => void;
  allCameras: ExtendedCamera[];
}) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {cameras.map((camera) => {
        const isEnabled = camera.monitored ?? false;

        return (
          <Card
            key={camera.id}
            className={cn(
              'overflow-hidden rounded-lg border-2 border-gray-700 bg-card2 transition-all hover:border-gray-600',
              !isEnabled && 'opacity-70'
            )}
          >
              <CardContent className="p-0">
                <div className="relative mb-3 flex aspect-video items-center justify-center overflow-hidden bg-gray-800">
                  <DeviceSnapshot deviceId={camera.id} className="h-full w-full object-cover" />

                  <div className="absolute bottom-0 left-0 right-0 p-2">
                    <DeviceCapabilitiesGroup capabilities={camera.capabilities} />
                  </div>
                </div>

                <div className="space-y-2 px-2 pb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="truncate text-lg font-semibold text-white">
                      {camera.name || 'Unnamed Camera'}
                    </h3>
                    <Link to={RoutePath.DeviceCamera(deviceId, camera.id ?? '')}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 flex-shrink-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>

                  <div className="line-clamp-1 text-sm text-gray-400">
                    {[camera.vendor, camera.model].filter(Boolean).join(' \u2022 ')}
                  </div>
                  
                  <div className="mt-2">
                    <ComponentCounts camera={camera as ExtendedCamera} onManageComponents={onManageComponents} allDevices={allCameras} />
                  </div>
                </div>
                <CameraGalleryToggle camera={camera} onToggle={onCameraToggle} />
              </CardContent>
            </Card>
        );
      })}
    </div>
  );
}

const useViewModeStore = create(
  persist(
    () => {
      const state: { mode: ViewMode } = { mode: 'table' };

      return state;
    },
    {
      name: 'view-mode',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
// DEPRECATED: ComponentManagementModal has been replaced by type-specific modals
// (SpeakerComponentsModal, LensComponentsModal, RelayComponentsModal)
// This function was removed to avoid syntax errors and is no longer needed.

export function CameraList({
  deviceId,
  projectId,
  cameras,
  onCameraToggle,
  onCameraToggleMany,
}: CameraListProps) {
  const viewMode = useViewModeStore((state) => state.mode);
  const cameraToggle = useDeviceFilterToggle({
    cameras,
    onToggle: (ids, enabled) => {
      onCameraToggleMany?.(ids, enabled);
    },
  });
  const PAGE_SIZE_OPTIONS = [5, 10, 20, 'all'] as const;
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(10);
  const [page, setPage] = useState(1);
  const [selectedCamera, setSelectedCamera] = useState<ExtendedCamera | null>(null);
  const [selectedComponentType, setSelectedComponentType] = useState<ComponentType | null>(null);
  const [liveTesterCameraId, setLiveTesterCameraId] = useState<string | null>(null);

  const handleManageComponents = (camera: ExtendedCamera, type: ComponentType) => {
    setSelectedCamera(camera);
    setSelectedComponentType(type);
  };

  const effectivePageSize = pageSize === 'all' ? Infinity : pageSize;
  const totalPages = Math.ceil(
    pageSize === 'all'
      ? 1
      : cameraToggle.filteredCameras.length / effectivePageSize,
  );
  const pagedCameras = useMemo(
    () =>
      pageSize === 'all'
        ? cameraToggle.filteredCameras
        : cameraToggle.filteredCameras.slice(
            (page - 1) * effectivePageSize,
            page * effectivePageSize,
          ),
    [cameraToggle.filteredCameras, page, pageSize],
  );

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-card1 p-4 rounded-xl border">
        <div className="flex w-full items-center gap-x-4">
          <div className="relative w-full max-w-md">
            <Input
              type="text"
              placeholder="Search cameras by name, vendor, or model..."
              value={cameraToggle.searchQuery}
              onChange={(e) => cameraToggle.setSearchQuery(e.target.value)}
              className="border-gray-600 pl-10 text-white placeholder-gray-400"
            />
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
          </div>
          <Button
            variant="blurred"
            size="sm"
            disabled={!cameraToggle.isFiltering}
            onClick={cameraToggle.onClear}
            className={cn({ hidden: !cameraToggle.isFiltering })}
          >
            <X /> Clear filter
          </Button>
          <p className="mt-1 text-gray-400"></p>
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center rounded-lg bg-card2 p-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => useViewModeStore.setState({ mode: 'table' })}
              className={cn(
                'rounded-r-none px-3',
                viewMode === 'table' ? 'bg-muted/30 opacity-100' : 'opacity-80'
              )}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => useViewModeStore.setState({ mode: 'gallery' })}
              className={cn(
                'rounded-l-none px-3',
                viewMode === 'gallery' ? 'bg-muted/30 opacity-100' : 'opacity-80'
              )}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
          </div>

          {projectId && <MoveDeviceButton deviceId={deviceId} currentProjectId={projectId} />}
          <RefreshCamerasButton deviceId={deviceId} />
        </div>
      </div>

      {/* Camera List */}
      {cameraToggle.filteredCameras.length === 0 ? (
        <div className="py-12 text-center">
          <Camera className="mx-auto mb-4 h-12 w-12 text-gray-500" />
          <h3 className="mb-2 text-lg font-medium text-gray-300">
            {cameras.length === 0 ? 'No cameras found' : 'No cameras match your search'}
          </h3>
          <p className="text-gray-500">
            {cameras.length === 0
              ? 'Connect cameras to this device to see them here.'
              : 'Try adjusting your search terms.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-x-4 pr-4">
            <Button
              variant="outline"
              className={cn({
                'bg-muted/10': cameraToggle.isFiltering && cameraToggle.selectMode === 'select',
              })}
              onClick={cameraToggle.onToggleFilteredCameras}
            >
              {cameraToggle.selectMode === 'select' ? 'Enable' : 'Disable'}
              {cameraToggle.isFiltering
                ? ` ${cameraToggle.filteredCameras.length} selected camera${cameraToggle.filteredCameras.length === 1 ? '' : 's'}`
                : ' all cameras'}
            </Button>

            <p className="text-sm font-medium opacity-80 px-4 border rounded-full py-1">
              {cameraToggle.filteredCameras.length}/{cameras.length} cameras
            </p>
          </div>
          {viewMode === 'table' ? (
            <CameraTableView
              cameras={pagedCameras}
              onCameraToggle={onCameraToggle}
              deviceId={deviceId}
              onManageComponents={handleManageComponents}
              allCameras={cameraToggle.filteredCameras as ExtendedCamera[]}
              onLiveTest={(id) => setLiveTesterCameraId(id)}
            />
          ) : (
            <CameraGalleryView
              cameras={pagedCameras}
              onCameraToggle={onCameraToggle}
              deviceId={deviceId}
              onManageComponents={handleManageComponents}
              allCameras={cameraToggle.filteredCameras as ExtendedCamera[]}
            />
          )}
          <div className="flex items-center justify-between">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <label htmlFor="page-size-select">Per page</label>
              <select
                id="page-size-select"
                value={pageSize}
                onChange={(e) =>
                  setPageSize(
                    e.target.value === 'all'
                      ? 'all'
                      : (parseInt(e.target.value) as 5 | 10 | 20),
                  )
                }
                className="rounded border border-gray-600 bg-transparent px-2 py-1"
              >
                {PAGE_SIZE_OPTIONS.filter((o) => o !== 'all').map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Type-specific Component Modals */}
      {selectedCamera && selectedComponentType === 'speakers' && selectedCamera.id && (
        <SpeakerComponentsModal
          deviceId={deviceId}
          subDeviceId={selectedCamera.id}
          open={!!selectedCamera}
          onOpenChange={(open) => !open && setSelectedCamera(null)}
          onClose={() => {
            setSelectedCamera(null);
            setSelectedComponentType(null);
          }}
        />
      )}

      {selectedCamera && selectedComponentType === 'lenses' && selectedCamera.id && (
        <LensComponentsModal
          deviceId={deviceId}
          subDeviceId={selectedCamera.id}
          open={!!selectedCamera}
          onOpenChange={(open) => !open && setSelectedCamera(null)}
          onClose={() => {
            setSelectedCamera(null);
            setSelectedComponentType(null);
          }}
        />
      )}

      {selectedCamera && selectedComponentType === 'relays' && selectedCamera.id && (
        <RelayComponentsModal
          deviceId={deviceId}
          subDeviceId={selectedCamera.id}
          open={!!selectedCamera}
          onOpenChange={(open) => !open && setSelectedCamera(null)}
          onClose={() => {
            setSelectedCamera(null);
            setSelectedComponentType(null);
          }}
        />
      )}

      {liveTesterCameraId && (
        <LiveTesterDialog
          open={!!liveTesterCameraId}
          onOpenChange={(open) => !open && setLiveTesterCameraId(null)}
          deviceId={deviceId}
          cameraId={liveTesterCameraId}
        />
      )}
    </div>
  );
}